/**
 * What a connection row on Settings › Warehouses offers (ACT2-1031), as data the shared `KebabMenu`
 * renders.
 *
 * Who owns the account decides the menu. One a person connected from RudderStack is theirs to manage,
 * so Lookout offers View, Choose RudderStack account and Detach. One Lookout owns offers View, Rotate
 * key, Choose RudderStack account and Delete. A row that needs attention offers its remedy beside the
 * menu (Reconnect, or Connect) and View and Delete inside it. Blocked actions stay visible, disabled,
 * with the reason.
 *
 * Choose RudderStack account moves a working connection onto another account without deleting it, so
 * the dashboards and routines that reference it keep working.
 *
 * Run orientation (ACT2-1340) appears only when the connection's last background orientation run did not
 * fully succeed (failed, blocked, partial or interrupted): the server's `canRetry`.
 */
import { Compass, Eye, KeyRound, Link2, PlugZap, ShieldOff, Trash2, Unlink } from 'lucide-react';

import type { KebabMenuAction } from '@/_shared/components/list/KebabMenu';

import type { WarehouseAccount } from './useWarehouseAccounts';
import type { ConnectionRow } from './warehouseList';

export const READ_ONLY_LINK_REASON =
  'The RudderStack connection is read-only. Reconnect RudderStack with a read-write token to do this.';
export const RUN_ORIENTATION_ADMIN_ONLY = 'Only workspace admins can run orientation';
const CONVERSION_NOT_ENABLED_REASON =
  'Moving this connection off the shared workspace key onto its own key is not available for this workspace yet.';

/**
 * Why an action that changes RudderStack (Rotate, Revoke, Reconnect) is unavailable, or null.
 * `manageBlocked` is the RudderStack permission's reason (ACT2-1276).
 */
export function rudderstackActionBlocked(manageBlocked: string | null, rudderstackReadOnly: boolean): string | null {
  if (manageBlocked !== null) return manageBlocked;
  if (rudderstackReadOnly) return READ_ONLY_LINK_REASON;
  return null;
}

/**
 * Why Rotate is unavailable, on top of the reasons every RudderStack-side action shares.
 *
 * A LEGACY row (`keySource === 'workspace'`) is rotated by converting it off the workspace's shared key,
 * and that conversion has its own deployment switch; the server refuses `conversion_not_enabled`, so
 * the reason is named up front. `isLegacyKeyConversionEnabled` is the server's resolved answer from
 * `auth.me`, never re-derived here. A row RudderStack already holds the key for rotates in place.
 */
export function rotateBlocked(
  account: Pick<WarehouseAccount, 'keySource'>,
  rudderstackBlocked: string | null,
  legacyKeyConversionEnabled: boolean,
): string | null {
  if (rudderstackBlocked !== null) return rudderstackBlocked;
  if (account.keySource === 'workspace' && !legacyKeyConversionEnabled) return CONVERSION_NOT_ENABLED_REASON;
  return null;
}

export interface ConnectionActionContext {
  /** ACT2-1276: why the RudderStack permission blocks every warehouse action, or null. */
  manageBlocked: string | null;
  rudderstackReadOnly: boolean;
  isLegacyKeyConversionEnabled: boolean;
  /**
   * `LOOKOUT_PLATFORM_ACCOUNT_REVOKE`. Off hides Revoke rather than disabling it: the switch is off for
   * a server-side defect no admin can act on, so a refused item would offer what nobody can have.
   */
  isPlatformAccountRevokeEnabled: boolean;
  onView: (account: WarehouseAccount) => void;
  onRotate: (account: WarehouseAccount) => void;
  onRevoke: (account: WarehouseAccount) => void;
  onDelete: (account: WarehouseAccount) => void;
  onDetach: (row: ConnectionRow) => void;
  onChooseAccount: (account: WarehouseAccount) => void;
  /**
   * Dial the saved connection now (ACT2-1128). The status badge reads saved configuration only, so
   * this is the one way on the page to learn whether the warehouse still answers.
   */
  onTest: (account: WarehouseAccount) => void;
  /** ACT2-1340: owner or admin. Run orientation charges the workspace, so only they may start it. */
  isAdmin: boolean;
  /** ACT2-1340: test the connection, then queue its background orientation run again. */
  onRunOrientation: (account: WarehouseAccount) => void;
  /**
   * The RudderStack accounts overview has not answered yet. Ownership and health both read it, so
   * until it lands the row offers View alone rather than actions for an owner it has not seen.
   */
  statusPending: boolean;
}

function blockable(
  action: Omit<KebabMenuAction, 'disabled' | 'disabledReason'>,
  reason: string | null,
): KebabMenuAction {
  return reason === null ? action : { ...action, disabled: true, disabledReason: reason };
}

export function connectionRowActions(row: ConnectionRow, ctx: ConnectionActionContext): KebabMenuAction[] {
  const { account } = row;
  const manageBlocked = ctx.manageBlocked;
  const rudderstackBlocked = rudderstackActionBlocked(ctx.manageBlocked, ctx.rudderstackReadOnly);
  // Test connection needs only the connection key, so it does not wait for the accounts overview.
  // The server gates it like every action, so the item says why instead of failing.
  const actions: KebabMenuAction[] = [
    { label: 'View', icon: Eye, onSelect: () => ctx.onView(account) },
    blockable({ label: 'Test connection', icon: PlugZap, onSelect: () => ctx.onTest(account) }, manageBlocked),
  ];
  // It tests the connection first, so the RudderStack permission that Test needs blocks it too.
  if (account.orientation?.canRetry === true) {
    actions.push(
      blockable(
        { label: 'Run orientation', icon: Compass, onSelect: () => ctx.onRunOrientation(account) },
        ctx.isAdmin ? manageBlocked : RUN_ORIENTATION_ADMIN_ONLY,
      ),
    );
  }
  if (ctx.statusPending) return actions;
  const chooseAccount = blockable(
    { label: 'Choose RudderStack account', icon: Link2, onSelect: () => ctx.onChooseAccount(account) },
    manageBlocked,
  );

  if (row.health.healthy && row.ownedByRudderStack) {
    actions.push(
      chooseAccount,
      blockable({ label: 'Detach', icon: Unlink, destructive: true, onSelect: () => ctx.onDetach(row) }, manageBlocked),
    );
    return actions;
  }

  if (row.health.healthy && account.keySource) {
    actions.push(
      blockable(
        {
          label: account.keySource === 'workspace' ? 'Rotate to a per-connection key' : 'Rotate key',
          icon: KeyRound,
          onSelect: () => ctx.onRotate(account),
        },
        rotateBlocked(account, rudderstackBlocked, ctx.isLegacyKeyConversionEnabled),
      ),
    );
  }
  if (row.health.healthy) actions.push(chooseAccount);
  if (row.health.healthy && account.credentialSource === 'platform' && ctx.isPlatformAccountRevokeEnabled) {
    actions.push(
      blockable(
        { label: 'Revoke in RudderStack', icon: ShieldOff, destructive: true, onSelect: () => ctx.onRevoke(account) },
        rudderstackBlocked,
      ),
    );
  }
  actions.push(
    blockable(
      { label: 'Delete', icon: Trash2, destructive: true, onSelect: () => ctx.onDelete(account) },
      manageBlocked,
    ),
  );
  return actions;
}
